import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const slides = [
  "/images/hero.png",
  "/images/megaphone.png",
  "/images/process.png",
  "/images/contact.png",
  "/images/services.png",
];

const words = ["Scaling", "Brands", "Into", "Giants."];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};
const word = {
  hidden: { y: "110%", opacity: 0 },
  show: { y: "0%", opacity: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

export const Hero = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((i) => (i + 1) % slides.length);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const scrollTo = (id) =>
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-12 gap-10 items-center">
        {/* Text */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-yellow-400" />
            <span className="text-xs tracking-[0.3em] uppercase text-yellow-400 font-semibold">
              Digital Advertising & Brand Scaling
            </span>
          </motion.div>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="font-display font-bold uppercase leading-[0.92] tracking-tight text-white text-5xl sm:text-7xl lg:text-8xl"
          >
            {words.map((w, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  variants={word}
                  className={`inline-block ${w === "Giants." ? "text-yellow-400" : ""}`}
                >
                  {w}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-8 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed"
          >
            Fat Head Advertisements is a studio built to make small brands
            impossible to ignore. Sharp campaigns, relentless growth, zero fluff.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("#contact")}
              className="group inline-flex items-center justify-center gap-2 bg-yellow-400 text-black hover:bg-yellow-300 px-8 py-4 text-sm uppercase tracking-widest font-bold transition-colors"
            >
              Start Scaling
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("#services")}
              className="inline-flex items-center justify-center gap-2 border border-zinc-800 text-white hover:border-yellow-400 hover:text-yellow-400 px-8 py-4 text-sm uppercase tracking-widest transition-colors"
            >
              What We Do
            </button>
          </motion.div>
        </div>

        {/* Slideshow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative"
        >
          <div
            data-testid="hero-slideshow"
            className="relative bg-black aspect-[4/3] w-full overflow-hidden"
          >
            <AnimatePresence>
              <motion.img
                key={active}
                data-testid="hero-image"
                src={slides[active]}
                alt="Fat Head Advertisements character"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.1, ease: "easeInOut" }}
                className="absolute inset-0 w-full h-full object-contain select-none"
                draggable={false}
              />
            </AnimatePresence>
          </div>

          <div className="mt-6 flex items-center gap-2" data-testid="hero-slide-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                data-testid={`hero-slide-dot-${i}`}
                onClick={() => setActive(i)}
                aria-label={`Show slide ${i + 1}`}
                className={`h-1 transition-all duration-500 ${
                  i === active ? "w-8 bg-yellow-400" : "w-4 bg-zinc-700 hover:bg-zinc-500"
                }`}
              />
            ))}
          </div>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollTo("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-zinc-500 hover:text-yellow-400 transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.button>
    </section>
  );
};

export default Hero;
